/**
 * GLAMRDiP PDP back-link memory
 * Joel 2026-05-07.
 *
 * Two responsibilities, both no-ops when the relevant DOM isn't on the
 * page:
 *
 *   COLLECTION SIDE — when a user clicks a product card link, capture
 *   `window.location.pathname + window.location.search` to
 *   sessionStorage. This preserves filter URL params (and any other
 *   query string the merchant may use) at the moment of navigation.
 *
 *   PDP SIDE — read sessionStorage on load. If a stored value exists
 *   AND points at a /collections/ URL, override the
 *   [data-gd-pdp-back-link] anchor's `href` so the breadcrumb takes
 *   the user back to the EXACT filtered state. Falls through to the
 *   anchor's existing hardcoded href otherwise (direct PDP visits,
 *   PDPs reached via search results, new tab opens, etc.).
 *
 * Why sessionStorage (not localStorage):
 *   - Per-tab scope. New tabs get a fresh sessionStorage, so opening
 *     a PDP in a new tab doesn't inherit the back URL from another
 *     tab's collection visit.
 *   - Cleared on tab close, no long-lived state.
 *
 * Edge cases handled:
 *   - Stored URL must include '/collections/' before override fires.
 *     Guards against any stale value pointing at a non-collection
 *     page.
 *   - sessionStorage may throw (private browsing, quota, disabled).
 *     Both read + write wrapped in try/catch.
 *   - Multiple back-link anchors on the same page (e.g. duplicated
 *     during section reload) — querySelectorAll + forEach.
 */
(() => {
  'use strict';

  const STORAGE_KEY = 'gd_last_collection_url';

  // ─── COLLECTION SIDE: capture URL on product-card click ───
  // The collection grid wraps each card body in a `.gd-colour-card__link`
  // anchor. Click delegation on the document level catches all current
  // and future cards (covers any AJAX section reloads that re-render
  // the grid without re-running this script).
  function isCollectionPage() {
    return window.location.pathname.includes('/collections/');
  }

  if (isCollectionPage()) {
    document.addEventListener('click', (e) => {
      // Find the nearest anchor going to a product PDP.
      const a = e.target && e.target.closest && e.target.closest('a');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (!href.includes('/products/')) return;
      try {
        sessionStorage.setItem(STORAGE_KEY, window.location.pathname + window.location.search);
      } catch {}
    }, true);   // capture phase so we run before the anchor's own click handlers
  }

  // ─── PDP SIDE: override breadcrumb back-link href on load ───
  function applyBackLinkOverride() {
    const links = document.querySelectorAll('[data-gd-pdp-back-link]');
    if (!links.length) return;
    let stored;
    try {
      stored = sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return;
    }
    if (!stored || !stored.includes('/collections/')) return;
    links.forEach((a) => {
      a.setAttribute('href', stored);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyBackLinkOverride, { once: true });
  } else {
    applyBackLinkOverride();
  }
  // Re-apply on Shopify section editor reload — the breadcrumb
  // anchor may be re-rendered without script re-run.
  document.addEventListener('shopify:section:load', applyBackLinkOverride);
})();
